/** UI-only client state (§2.1): filters, panels, theme. Never server data. */

import { create } from 'zustand';

export type Theme = 'light' | 'dark' | 'system';
export type Density = 'comfortable' | 'compact';

interface UiState {
  sidebarCollapsed: boolean;
  theme: Theme;
  density: Density;
  commandPaletteOpen: boolean;
  quickAddOpen: boolean;
  shortcutSheetOpen: boolean;
  createProjectOpen: boolean;
  newTaskOpen: boolean;
  notificationsOpen: boolean;
  /**
   * V8-03 §2 — the mobile navigation drawer.
   *
   * Deliberately *not* `sidebarCollapsed`. That one is the desktop width toggle
   * (56px ↔ 240px) and is persisted; below `md` the sidebar is `display:none`,
   * so flipping it there changed the width of something nobody could see —
   * which is precisely why tapping the hamburger appeared to do nothing.
   *
   * Not persisted: a drawer that is open when you next open the app is a
   * drawer in the way.
   */
  mobileNavOpen: boolean;
  toggleSidebar: () => void;
  setMobileNavOpen: (open: boolean) => void;
  setTheme: (theme: Theme) => void;
  setDensity: (density: Density) => void;
  setCommandPaletteOpen: (open: boolean) => void;
  setQuickAddOpen: (open: boolean) => void;
  setShortcutSheetOpen: (open: boolean) => void;
  setCreateProjectOpen: (open: boolean) => void;
  setNewTaskOpen: (open: boolean) => void;
  setNotificationsOpen: (open: boolean) => void;
}

function readStoredTheme(): Theme {
  const raw = localStorage.getItem('vt-theme');
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
}

function readStoredDensity(): Density {
  return localStorage.getItem('vt-density') === 'compact' ? 'compact' : 'comfortable';
}

export function applyTheme(theme: Theme): void {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const dark = theme === 'dark' || (theme === 'system' && prefersDark);
  document.documentElement.classList.toggle('dark', dark);
  localStorage.setItem('vt-theme', theme);
}

export function applyDensity(density: Density): void {
  document.documentElement.setAttribute('data-density', density);
  localStorage.setItem('vt-density', density);
}

export const useUiStore = create<UiState>((set, get) => ({
  sidebarCollapsed: localStorage.getItem('vt-sidebar') === 'collapsed',
  theme: readStoredTheme(),
  density: readStoredDensity(),
  commandPaletteOpen: false,
  quickAddOpen: false,
  shortcutSheetOpen: false,
  createProjectOpen: false,
  newTaskOpen: false,
  notificationsOpen: false,
  mobileNavOpen: false,

  setMobileNavOpen: (open) => set({ mobileNavOpen: open }),

  toggleSidebar: () => {
    const next = !get().sidebarCollapsed;
    localStorage.setItem('vt-sidebar', next ? 'collapsed' : 'expanded');
    set({ sidebarCollapsed: next });
  },
  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
  },
  setDensity: (density) => {
    applyDensity(density);
    set({ density });
  },
  setCommandPaletteOpen: (commandPaletteOpen) => set({ commandPaletteOpen }),
  setQuickAddOpen: (quickAddOpen) => set({ quickAddOpen }),
  setShortcutSheetOpen: (shortcutSheetOpen) => set({ shortcutSheetOpen }),
  setCreateProjectOpen: (createProjectOpen) => set({ createProjectOpen }),
  setNewTaskOpen: (newTaskOpen) => set({ newTaskOpen }),
  setNotificationsOpen: (notificationsOpen) => set({ notificationsOpen }),
}));
